import * as React from 'react';
import { batch, shallowEqual, useDispatch, useSelector } from 'react-redux';

import { openModalRemove } from '../../../actions/folders/actions';
import { closeShareModal } from '../../../actions/shareCollection';
import { Banner, SettingsModalContent } from '../../../components/ShareSettingsModal';
import { useFavoritesUxfeedbackCampaign } from '../../../hooks/uxfeedback';
import { selectOpenedShareCollection, selectResults } from '../../../selectors';
import { selectIsSearcherOrSub } from '../../../selectors/lkType/selectIsSearcherOrSub';
import { EShareOptions, shareFolder } from '../../../thunk/favorites';
import { trackSaveSettings } from '../tracking';

export const SettingsModalContentContainer: React.FunctionComponent<{}> = () => {
  const openedShareCollection = useSelector(selectOpenedShareCollection, shallowEqual);
  const isSearcherOrSub = useSelector(selectIsSearcherOrSub);
  const { favoriteEntities } = useSelector(selectResults);

  const { sendFavoritesUxfeedbackCampaign } = useFavoritesUxfeedbackCampaign();

  const {
    name = '',
    hideNotes = false,
    hideAddresses = false,
    hideOffersLink = true,
    isFetching,
  } = openedShareCollection || {};

  const dispatch = useDispatch();

  const [hideNotesDraft, sethideNotesDraft] = React.useState(hideNotes);
  const [hideAddressesDraft, sethideAddressesDraft] = React.useState(hideAddresses);
  const [hideOffersLinkDraft, sethideOffersLinkDraft] = React.useState(hideOffersLink);
  const [nameDraft, setNameDraft] = React.useState(name);

  const isLoading = isFetching === EShareOptions.SaveSettings;

  const handleNameChange = React.useCallback((value: string) => {
    setNameDraft(value);
  }, []);

  const handleHideNotesChange = React.useCallback((value: boolean) => {
    sethideNotesDraft(value);
  }, []);

  const handleHideOffersLinkChange = React.useCallback((value: boolean) => {
    sethideOffersLinkDraft(value);
  }, []);

  const handleHideAddressesChange = React.useCallback((value: boolean) => {
    sethideAddressesDraft(value);
  }, []);

  const handleSaveSettingsClick = React.useCallback(() => {
    if (!isLoading) {
      sendFavoritesUxfeedbackCampaign();
      dispatch(
        shareFolder(EShareOptions.SaveSettings, {
          name: nameDraft,
          hideAddress: hideAddressesDraft,
          hideComments: hideNotesDraft,
          hideOffersLink: hideOffersLinkDraft,
        }),
      );
      trackSaveSettings(favoriteEntities, {
        with_notes: !hideNotesDraft,
        with_links: !hideOffersLinkDraft,
        with_addresses: !hideAddressesDraft,
      });
    }
  }, [
    dispatch,
    favoriteEntities,
    hideAddressesDraft,
    hideNotesDraft,
    hideOffersLinkDraft,
    isLoading,
    nameDraft,
    sendFavoritesUxfeedbackCampaign,
  ]);

  const handleDeleteClick = React.useCallback(() => {
    batch(() => {
      dispatch(closeShareModal());
      dispatch(openModalRemove());
    });
  }, [dispatch]);

  const handleClose = React.useCallback(() => {
    dispatch(closeShareModal());
  }, [dispatch]);

  if (!openedShareCollection) {
    return null;
  }

  const bannerElement = <Banner text="Контакты продавцов скрыты — клиент сможет связаться только с вами" />;

  return (
    <SettingsModalContent
      isSearcherOrSub={isSearcherOrSub}
      name={nameDraft}
      banner={!isSearcherOrSub && bannerElement}
      hideNotes={hideNotesDraft}
      hideOffersLink={hideOffersLinkDraft}
      hideAddresses={hideAddressesDraft}
      loading={isFetching === EShareOptions.SaveSettings}
      canSubmit={nameDraft.trim().length === 0}
      onNameChange={handleNameChange}
      onHideNotesChange={handleHideNotesChange}
      onHideOffersLinkChange={handleHideOffersLinkChange}
      onHideAddressesChange={handleHideAddressesChange}
      onSaveSettingsClick={handleSaveSettingsClick}
      onDeleteClick={handleDeleteClick}
      onCancel={handleClose}
    />
  );
};
